import { Fragment, useState, useMemo } from 'react';
import { List as ImmutableList } from 'immutable';
import { withRouter } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import partial from 'transmute/partial';
import SalesContentPartitioning from 'sales-content-partitioning';
import * as links from 'documents-lib/js/lib/links';
import { rethrowError } from 'UIComponents/core/PromiseHandlers';
import { canEditObjectPermissions, canWrite } from 'documents-ui/lib/permissions';
import { trackInteraction } from 'documents-lib/js/tracking/tracker';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { ActionsMenu } from 'foundations-components/components/ActionsMenu';
import DocumentsCreateButton from 'documents-ui/components/header/DocumentsCreateButton';
import CreateLinkPrompt from 'documents-ui/components/createLink/CreateLinkPrompt';
import DeleteModal from '../DeleteModal';
import ChangeBrandModal from 'documents-ui/components/brand/ChangeBrandModal';
import { useCampaignsPlatformEditActionMenuItem } from 'campaigns-lib/campaignsPlatform';
import { isMultiBrandEnabled } from 'documents-lib/js/utils/isMultiBrandEnabled';
import { UsageTrackingScreen } from '../../constants/UsageTracking';
import { CLICK_DELETE, CLICK_SHARE, CLICK_PREVIEW_SUMMARY, CLICK_REUPLOAD_SUMMARY, CLICK_CHANGE_BRAND } from 'documents-lib/js/tracking/actions';
import SearchResultRecord from 'SalesContentIndexUI/data/records/SearchResultRecord';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import IndexUIActions from 'SalesContentIndexUI/data/actions/IndexUIActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SummaryHeaderButtons = ({
  documentMetadata,
  gdprSettings,
  onPercentProgress,
  removeDocument,
  downloadDocument,
  uploadFile,
  importFile,
  history,
  location
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeBrandModal, setShowChangeBrandModal] = useState(false);
  const deckId = documentMetadata.get('deckId');
  const title = documentMetadata.get('title');
  const userId = documentMetadata.get('userId');
  const {
    showCreateLinkModal,
    renderCreateLinkModal
  } = CreateLinkPrompt({
    deckId,
    name: title || '',
    gdprSettings
  });
  const campaignsPlatformEditActionMenuItem = useCampaignsPlatformEditActionMenuItem({
    objectType: 'SALES_DOCUMENT',
    assetId: deckId,
    crmIdProperty: 'hs_document_id'
  });
  const handleDeleteConfirm = documentToDelete => {
    trackInteraction(CLICK_DELETE);
    removeDocument(documentToDelete).then(() => {
      // Optimistically remove the deleted document from the store. This ensures the deleted
      // document is removed from the UI immediately, addressing race conditions with the backend.
      IndexUIActions.removeResults(ImmutableList([documentToDelete.id]));
      history.push({
        pathname: '/'
      });
    }).catch(rethrowError);
  };
  const handleUploadRequest = (uploadRequest, config) => {
    uploadRequest(Object.assign({}, config, {
      deckId: deckId,
      onPercentProgress
    }));
  };
  const actions = useMemo(() => {
    const result = [{
      value: 'preview',
      text: I18n.text('summary.header.buttons.preview'),
      indicator: /*#__PURE__*/_jsx(ExternalLinkIcon, {})
    }, {
      value: 'download',
      text: I18n.text('summary.header.buttons.download')
    }];
    if (canWrite()) {
      result.push({
        value: 'delete',
        text: I18n.text('summary.header.buttons.delete')
      });
      if (isMultiBrandEnabled()) {
        result.push({
          value: 'changeBrand',
          text: I18n.text('summary.header.buttons.changeBrand')
        });
      }
    }
    if (canWrite() && userId && canEditObjectPermissions(userId)) {
      result.push({
        value: 'share',
        text: I18n.text('summary.header.buttons.share')
      });
    }
    return result.concat(campaignsPlatformEditActionMenuItem.actionMenuItems);
  }, [campaignsPlatformEditActionMenuItem.actionMenuItems, userId]);
  const handleActionClick = ({
    value
  }) => {
    if (value === 'preview') {
      trackInteraction(CLICK_PREVIEW_SUMMARY);

      // Open a preview in a new tab
      const previewLink = links.getPreviewURL(deckId || 0);
      const newWindow = window.open(previewLink);
      if (newWindow) newWindow.focus();
    } else if (value === 'download' && deckId && title) {
      downloadDocument({
        id: deckId,
        name: title
      });
    } else if (value === 'delete') {
      setShowDeleteModal(true);
    } else if (value === 'changeBrand') {
      trackInteraction(CLICK_CHANGE_BRAND, UsageTrackingScreen.DETAIL);
      setShowChangeBrandModal(true);
    } else if (value === 'campaignsPlaformEditAction') {
      // campaignsPlaformEditAction is the hardcoded value from campaigns-lib
      campaignsPlatformEditActionMenuItem.onCampaignEdit();
    } else if (value === 'share') {
      SalesContentPartitioning.openAssignmentPanel({
        objectId: deckId,
        objectType: 'DOCUMENT',
        ownerId: documentMetadata.get('userId')
      });
    }
  };
  const renderDeleteModal = () => {
    const user = documentMetadata.get('user');
    const documentToDelete = SearchResultRecord.initFromJS({
      id: deckId === null || deckId === void 0 ? void 0 : deckId.toString(),
      contentId: deckId,
      contentType: 'DOCUMENT',
      name: title,
      userId,
      userView: user
    });
    return showDeleteModal && /*#__PURE__*/_jsx(DeleteModal, {
      onConfirm: () => {
        setShowDeleteModal(false);
        handleDeleteConfirm(documentToDelete);
      },
      onReject: () => {
        setShowDeleteModal(false);
      },
      documentsToDelete: ImmutableList([documentToDelete])
    });
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: "end",
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 0,
        children: /*#__PURE__*/_jsx(ActionsMenu, {
          "data-selenium-test": "summary-actions-button",
          referenceSize: "compact",
          use: "secondary",
          referenceText: I18n.text('summary.header.buttons.actions'),
          menuInlineSize: 150,
          onActionClick: handleActionClick,
          actions: actions
        })
      }), /*#__PURE__*/_jsx(DocumentsCreateButton, {
        documentMetadata: documentMetadata,
        isReupload: true,
        uploadFile: partial(handleUploadRequest, uploadFile),
        importFile: partial(handleUploadRequest, importFile),
        buttonSize: "small",
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "headerButtons.createButton.reupload"
        }),
        buttonUse: "secondary",
        onClick: () => trackInteraction(CLICK_REUPLOAD_SUMMARY),
        location: location,
        portalIsAtLimit: false // reupload only
      }), /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "share-document-button",
        marginInlineStart: "300",
        size: "small",
        use: "primary",
        onClick: () => {
          trackInteraction(CLICK_SHARE);
          showCreateLinkModal();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "summary.header.buttons.createLink"
        })
      })]
    }), renderDeleteModal(), renderCreateLinkModal(), showChangeBrandModal && /*#__PURE__*/_jsx(ChangeBrandModal, {
      documentMetadata: documentMetadata,
      onSave: () => setShowChangeBrandModal(false),
      onReject: () => setShowChangeBrandModal(false)
    })]
  });
};
export default withRouter(SummaryHeaderButtons);